import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  LocalPrintshop as LocalPrintshopIcon,
} from '@mui/icons-material';

import { useDymo } from '../contexts/DymoContext';

const TEST_CHARGE = 'TEST-CHARGE';
const TEST_COIL = '001';

type PrintStatus = 'idle' | 'printing' | 'success' | 'error';

function StatusRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <Typography color="text.secondary">{label}</Typography>
      <Chip
        size="small"
        color={ok ? 'success' : 'error'}
        icon={ok ? <CheckCircleIcon /> : <ErrorIcon />}
        label={ok ? 'Ja' : 'Nein'}
      />
    </Paper>
  );
}

export default function DymoTestPage() {
  const dymo = useDymo();
  const ensureReady = dymo.ensureReady;
  const [selectedPrinter, setSelectedPrinter] = useState('');

  useEffect(() => {
    void ensureReady();
  }, [ensureReady]);
  const [status, setStatus] = useState<PrintStatus>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const activePrinter = selectedPrinter || dymo.defaultPrinter?.name || '';

  function handlePrint() {
    setStatus('printing');
    setErrorMsg(null);
    const result = dymo.printCoilLabel(TEST_CHARGE, TEST_COIL);
    if (result.success) {
      setStatus('success');
      setTimeout(() => setStatus('idle'), 3000);
    } else {
      setStatus('error');
      setErrorMsg(result.error || 'Drucken fehlgeschlagen');
    }
  }

  const canPrint =
    dymo.isReady && dymo.isAvailable && dymo.printers.length > 0 && status !== 'printing';

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        DYMO Drucker Test
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Diese Seite prüft die Anbindung an DYMO Connect und druckt ein Testetikett mit der Charge{' '}
        <code>{TEST_CHARGE}</code> und Coil-Nr <code>{TEST_COIL}</code>.
      </Typography>

      <Card>
        <CardContent>
          <Stack spacing={4}>
            <Box>
              <Typography variant="h6" gutterBottom>
                DYMO Status
              </Typography>
              {!dymo.isReady ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography color="text.secondary">Initialisiere DYMO …</Typography>
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <StatusRow label="DYMO Framework geladen" ok={dymo.isAvailable} />
                  <StatusRow label="Browser unterstützt" ok={dymo.browserSupported} />
                  <StatusRow label="DYMO Connect installiert" ok={dymo.frameworkInstalled} />
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={dymo.refresh}
                    fullWidth
                  >
                    Status aktualisieren
                  </Button>
                </Stack>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Verfügbare Drucker
              </Typography>
              {!dymo.isReady ? null : dymo.printers.length === 0 ? (
                <Alert severity="warning">
                  Keine DYMO Drucker gefunden. Stelle sicher, dass DYMO Connect Software installiert
                  ist und ein DYMO Drucker per USB angeschlossen ist.
                </Alert>
              ) : (
                <RadioGroup
                  value={activePrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                >
                  <Stack spacing={1}>
                    {dymo.printers.map((p) => (
                      <Paper key={p.name} variant="outlined" sx={{ p: 1.5 }}>
                        <FormControlLabel
                          value={p.name}
                          control={<Radio />}
                          sx={{ width: '100%', m: 0 }}
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <Box>
                                <Typography fontWeight={500}>{p.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {p.model}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                color={p.isConnected ? 'success' : 'error'}
                                label={p.isConnected ? 'Verbunden' : 'Getrennt'}
                              />
                            </Box>
                          }
                        />
                      </Paper>
                    ))}
                  </Stack>
                </RadioGroup>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Test Druck
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Test Charge / Coil
                </Typography>
                <Typography variant="h5" fontFamily="monospace" fontWeight={700}>
                  {TEST_CHARGE} / {TEST_COIL}
                </Typography>
              </Paper>

              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={!canPrint}
                color={status === 'success' ? 'success' : status === 'error' ? 'error' : 'primary'}
                startIcon={
                  status === 'printing' ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <LocalPrintshopIcon />
                  )
                }
                onClick={handlePrint}
              >
                {status === 'printing'
                  ? 'Drucke …'
                  : status === 'success'
                    ? 'Erfolgreich gedruckt!'
                    : status === 'error'
                      ? 'Druck fehlgeschlagen — erneut versuchen'
                      : 'Test Etikett drucken'}
              </Button>

              {errorMsg && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errorMsg}
                </Alert>
              )}
            </Box>

            {dymo.isReady && !dymo.isAvailable && (
              <Alert severity="info">
                <Typography fontWeight={500} gutterBottom>
                  Einrichtung
                </Typography>
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  <li>
                    <strong>DYMO Connect Software</strong> von dymo.com installieren
                  </li>
                  <li>DYMO LabelWriter Drucker per USB anschließen</li>
                  <li>DYMO Connect öffnen und prüfen, dass der Drucker erkannt wird</li>
                  <li>Diese Seite neu laden</li>
                </ol>
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
